import React, { useState } from 'react';
import {
  CreditCard, DollarSign, TrendingUp, Download, Calendar,
  CheckCircle, Clock, AlertTriangle, ArrowUpRight, Filter,
  FileText, Building2, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';

const revenueData = {
  mrr: '$284,500',
  arr: '$3,414,000',
  growth: '+18.5%',
  avgRevenuePerFacility: '$6,053',
};

const plans = [
  { name: 'Starter', price: '$1,500', facilities: 8, color: 'gray' },
  { name: 'Professional', price: '$4,500', facilities: 15, color: 'blue' },
  { name: 'Enterprise', price: '$12,000+', facilities: 24, color: 'teal' },
];

const recentTransactions = [
  { id: 1, facility: 'Community Hospital Network', amount: '$24,000', status: 'paid', date: 'Aug 1, 2025', type: 'subscription' },
  { id: 2, facility: 'Valley Health Partners', amount: '$15,000', status: 'paid', date: 'Aug 1, 2025', type: 'subscription' },
  { id: 3, facility: 'Metro Health System', amount: '$8,500', status: 'paid', date: 'Aug 1, 2025', type: 'subscription' },
  { id: 4, facility: 'Regional Medical Center', amount: '$6,500', status: 'pending', date: 'Aug 5, 2025', type: 'subscription' },
  { id: 5, facility: 'Coastal Rehab Center', amount: '$4,200', status: 'paid', date: 'Aug 1, 2025', type: 'subscription' },
  { id: 6, facility: 'Sunrise Long-Term Care', amount: '$2,500', status: 'overdue', date: 'Jul 28, 2025', type: 'subscription' },
];

const invoices = [
  { id: 'INV-2025-0847', facility: 'Metro Health System', amount: '$8,500', status: 'paid', dueDate: 'Aug 15, 2025' },
  { id: 'INV-2025-0846', facility: 'Community Hospital', amount: '$24,000', status: 'paid', dueDate: 'Aug 10, 2025' },
  { id: 'INV-2025-0845', facility: 'Valley Health', amount: '$15,000', status: 'pending', dueDate: 'Aug 20, 2025' },
  { id: 'INV-2025-0844', facility: 'Sunrise LTC', amount: '$2,500', status: 'overdue', dueDate: 'Jul 28, 2025' },
];

const BillingPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Billing & Revenue</h1>
          <p className="text-gray-500 mt-1">Manage subscriptions, invoices, and revenue analytics</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button className="btn-primary gap-2">
            <FileText className="w-4 h-4" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-teal-dark)] rounded-2xl p-6 text-white">
          <div className="flex items-center gap-2 mb-2 text-white/70">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm">Monthly Revenue</span>
          </div>
          <p className="text-3xl font-bold">{revenueData.mrr}</p>
          <div className="flex items-center gap-1 mt-2 text-[var(--niko-green)]">
            <ArrowUpRight className="w-4 h-4" />
            <span className="text-sm font-medium">{revenueData.growth} vs last month</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-gray-500">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Annual Revenue</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{revenueData.arr}</p>
          <p className="text-sm text-gray-500 mt-2">Projected</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-gray-500">
            <Building2 className="w-4 h-4" />
            <span className="text-sm">Avg. per Facility</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{revenueData.avgRevenuePerFacility}</p>
          <p className="text-sm text-gray-500 mt-2">Per month</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-gray-500">
            <CreditCard className="w-4 h-4" />
            <span className="text-sm">Collection Rate</span>
          </div>
          <p className="text-3xl font-bold text-green-600">98.2%</p>
          <p className="text-sm text-gray-500 mt-2">Last 90 days</p>
        </div>
      </div>

      {/* Plans Overview */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Plans Distribution</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan.name} className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-gray-900">{plan.name}</span>
                <span className="text-[var(--niko-teal)] font-bold">{plan.price}/mo</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{plan.facilities} facilities</span>
                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] rounded-full"
                    style={{ width: `${(plan.facilities / 47) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {['overview', 'invoices', 'transactions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-[var(--niko-teal)] text-[var(--niko-teal)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Facility</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Amount</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Date</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentTransactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-gray-500" />
                      </div>
                      <span className="font-medium text-gray-900">{tx.facility}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-semibold text-gray-900">{tx.amount}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{tx.date}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      tx.status === 'paid'
                        ? 'bg-green-100 text-green-700'
                        : tx.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {tx.status === 'paid' && <CheckCircle className="w-3 h-3" />}
                      {tx.status === 'pending' && <Clock className="w-3 h-3" />}
                      {tx.status === 'overdue' && <AlertTriangle className="w-3 h-3" />}
                      {tx.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button className="text-gray-400 hover:text-[var(--niko-teal)]">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
